import { compact, filter, find, first, isEmpty, join, last, slice, sortBy, split, startsWith } from 'es-toolkit/compat';

import { defaultLayerColor, defaultLayerColors } from 'components/theme/theme';
import { CellColorMapping } from 'interfaces/cellColorMapping';
import { ResultType } from 'interfaces/experimentResults';
import { getStainingMethodByStainType, MULTIPLEX_STAIN_ID, StainingMethod } from 'interfaces/stainType';
import { Taxonomy } from 'interfaces/taxonomy';
import { getColorHex } from 'utils/helpers';

export const NUCLEAR_SEGMENTATION_VISUALIZATION_ID = '6';
export const NUCLEAR_SEGMENTATION_VISUALIZATION_TYPE_ID = 'nuclei';
export const defaultMultiplexNuclearSegmentationColor = defaultLayerColors[0];

const normalizeOverlayColor = (color?: string) => getColorHex(color)?.toUpperCase();

const genericMultiplexNuclearOverlayColors = new Set(
  compact([normalizeOverlayColor(defaultLayerColor), normalizeOverlayColor('#FFFFFF'), normalizeOverlayColor('#FFF')])
);

export const isNuclearSegmentationVisualization = ({
  visualizationId,
  visualizationTypeId,
  resultType,
}: {
  visualizationId?: string;
  visualizationTypeId?: string;
  resultType?: ResultType;
}) =>
  visualizationId === NUCLEAR_SEGMENTATION_VISUALIZATION_ID ||
  visualizationTypeId === NUCLEAR_SEGMENTATION_VISUALIZATION_TYPE_ID ||
  resultType === ResultType.SlideInferenceNucleiSegmentation;

export const getDefaultMultiplexNuclearSegmentationColor = ({
  stainTypeId,
  visualizationId,
  visualizationTypeId,
  resultType,
  matchingTypeDefaultColor,
}: {
  stainTypeId?: string;
  visualizationId?: string;
  visualizationTypeId?: string;
  resultType?: ResultType;
  matchingTypeDefaultColor?: string;
}) => {
  if (stainTypeId !== MULTIPLEX_STAIN_ID) {
    return undefined;
  }
  if (!isNuclearSegmentationVisualization({ visualizationId, visualizationTypeId, resultType })) {
    return undefined;
  }

  const candidateColor = matchingTypeDefaultColor;
  const normalizedCandidateColor = candidateColor ? normalizeOverlayColor(candidateColor) : undefined;
  if (!normalizedCandidateColor || genericMultiplexNuclearOverlayColors.has(normalizedCandidateColor)) {
    return defaultMultiplexNuclearSegmentationColor;
  }

  return undefined;
};

const doesCellColorMappingMatchClassName = (cellColorMapping: CellColorMapping, layerName: string) => {
  const sortedTags = sortBy(cellColorMapping.tags);
  const tagsString = join(sortedTags, '-');
  // the cellClassId is the whole taxonomy path, we need only the last part
  const cellType = last(split(cellColorMapping?.cellClassId, '.'));
  const layerNameFromCell = `${cellType}${tagsString ? `-${tagsString}` : ''}`;
  // sort also the tags inside the layer name
  const tagsFromLayerName = slice(split(layerName, '-'), 1);
  const sortedLayerName = `${first(split(layerName, '-'))}${!isEmpty(tagsFromLayerName) ? '-' : ''}${join(
    sortBy(tagsFromLayerName),
    '-'
  )}`;
  return sortedLayerName === layerNameFromCell;
};

export const getColorForCellClassificationLayer = ({
  layerName,
  taxonomies,
  cellColorMappings,
  stainTypeId,
}: {
  layerName: string;
  taxonomies?: Taxonomy[];
  cellColorMappings?: CellColorMapping[];
  stainTypeId: string;
}) => {
  const cellColorMappingsMatchingLayerName = filter(cellColorMappings, (cellColorMapping) =>
    doesCellColorMappingMatchClassName(cellColorMapping, layerName)
  );

  const colorMatchingFromCellColorMappings =
    // Find the first cellColorMapping that matches the stainTypeId
    (
      find(cellColorMappingsMatchingLayerName, (cellColorMapping) => cellColorMapping?.stainTypeId === stainTypeId) ||
      // If no match found, check if stainTypeId is ihc and try to find a cellColorMapping with ihc
      (getStainingMethodByStainType(stainTypeId) === StainingMethod.ihc &&
        find(
          cellColorMappingsMatchingLayerName,
          (cellColorMapping) => cellColorMapping?.stainTypeId === StainingMethod.ihc
        )) ||
      // If no match found, try to find a cellColorMapping without stainTypeId
      find(cellColorMappingsMatchingLayerName, (cellColorMapping) => isEmpty(cellColorMapping?.stainTypeId))
    )?.color;

  const taxonomiesWithColors = filter(taxonomies, 'defaultColor');
  const colorMatchingFromTaxonomy = (
    find(taxonomiesWithColors, (taxonomy) => taxonomy.path === layerName) ||
    find(taxonomiesWithColors, (taxonomy) => startsWith(layerName, `${taxonomy.path}-`)) ||
    find(taxonomiesWithColors, (taxonomy) => last(split(taxonomy.path, '.')) === layerName) ||
    find(taxonomiesWithColors, (taxonomy) => startsWith(layerName, `${last(split(taxonomy.path, '.'))}-`))
  )?.defaultColor;

  return colorMatchingFromCellColorMappings || colorMatchingFromTaxonomy;
};
